import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Autocomplete,
  Card,
  CardContent,
  Grid,
  Typography,
  Divider,
  InputAdornment,
  createTheme,
  ThemeProvider,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Fax as FaxIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Receipt as ReceiptIcon,
  Home as HomeIcon,
  Badge as BadgeIcon,
} from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import debounce from "lodash/debounce";
import config from "../../../config/config";
import { toast } from "react-toastify";
import { useAuth } from "../../login/AuthContext";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";

const initialNewCustomerState = {
  Unvan: "",
  Yetkili: "",
  Tel: "",
  Tel2: "",
  Fax: "",
  VergiDairesi: "",
  VergiNo: "",
  TCKimlikNo: "",
  EPosta: "",
  Il: "",
  Ilce: "",
  Adres: "",
};

const normalizeCustomerFields = (raw = {}) => ({
  ...initialNewCustomerState,
  ...raw,
  Fax: raw.Fax ?? raw.Faks ?? "",
  Faks: raw.Faks ?? raw.Fax ?? "",
});

const CustomerStep = ({
  onChange,
  initialData = {},
  orderId = null,
  skipOrderFetch = false,
  allowEditExistingCustomer = false,
  isDisabled,
  isEditMode,
  customer = {},
}) => {
  const { userData, token } = useAuth();
  const { musteriId } = useParams();
  const [data, setData] = useState(
    initialData.customer || initialNewCustomerState
  );
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(false);

  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [selectedIlId, setSelectedIlId] = useState("");

  useEffect(() => {
    const fetchIller = async () => {
      try {
        const url = config.baseURL.endsWith("/")
          ? `${config.baseURL}musteriler/form-bilgi/iller`
          : `${config.baseURL}/musteriler/form-bilgi/iller`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP hatası: ${response.status}`);
        }
        const data = await response.json();
        setIller(data);
      } catch (error) {
        console.error("İller yüklenirken hata:", error);
      }
    };

    fetchIller();
  }, [token]);

  // İlçeleri yükleyen useEffect
  useEffect(() => {
    const fetchIlceler = async () => {
      if (!selectedIlId) {
        setIlceler([]);
        return;
      }

      try {
        const url = config.baseURL.endsWith("/")
          ? `${config.baseURL}musteriler/form-bilgi/ilceler/${selectedIlId}`
          : `${config.baseURL}/musteriler/form-bilgi/ilceler/${selectedIlId}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP hatası: ${response.status}`);
        }

        const data = await response.json();
        setIlceler(data);
      } catch (error) {
        console.error("İlçeler yüklenirken hata:", error);
        setIlceler([]);
      }
    };

    fetchIlceler();
  }, [selectedIlId, token]);

  // İl ve ilçe verilerini ayarlayan useEffect - DÜZELTME 1
  useEffect(() => {
    if (data.Il && iller.length > 0) {
      const existingIl = iller.find((il) =>
        il.IlAdi.toLocaleLowerCase("tr-TR") === data.Il.toLocaleLowerCase("tr-TR")
      );
      if (existingIl) {
        const ilId = existingIl.PlakaKodu || existingIl.IlId;
        setSelectedIlId(ilId);
      }
    }
  }, [data.Il, iller]);

  // DÜZELTME 2: İlçeleri yükledikten sonra mevcut ilçeyi seçmek için
  useEffect(() => {
    if (data.Ilce && ilceler.length > 0 && selectedIlId) {
      // İlçe verisi zaten data'da var, sadece kontrol edelim
      const existingIlce = ilceler.find((ilce) =>
        ilce.IlceAdi.toLocaleLowerCase("tr-TR") === data.Ilce.toLocaleLowerCase("tr-TR") &&
        ilce.IlId === selectedIlId
      );

      if (!existingIlce && data.Ilce) {
        console.warn(`İlçe bulunamadı: ${data.Ilce} (İl ID: ${selectedIlId})`);
      }
    }
  }, [data.Ilce, ilceler, selectedIlId]);

  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 3) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      try {
        setIsSearching(true);
        const response = await axios.get(
          `${config.baseURL}/musteriler/search?query=${encodeURIComponent(
            query
          )}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSearchResults(response.data.data);
      } catch (error) {
        toast.error("Müşteri araması yapılırken bir hata oluştu");
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [token]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (musteriId) {
      fetchCustomerById(musteriId);
    } else if (orderId && !skipOrderFetch) {
      fetchOrderDetails();
    }
  }, [musteriId, orderId, skipOrderFetch]);

  // DÜZELTME 3: initialData değiştiğinde data'yı güncelle
  // Gastora / harici formlardan gelen müşteri verisini senkronize et (döngüyü önlemek için kontrollü)
  const lastHydratedKeyRef = useRef("");
  useEffect(() => {
    const newData = normalizeCustomerFields(initialData.customer || initialNewCustomerState);
    const hydrateKey = skipOrderFetch
      ? `${newData.MusteriNo || ""}|${newData.Unvan || ""}|${newData.Id || ""}`
      : "";
    if (skipOrderFetch && hydrateKey && lastHydratedKeyRef.current === hydrateKey) {
      return;
    }
    if (skipOrderFetch && hydrateKey) {
      lastHydratedKeyRef.current = hydrateKey;
    }

    setData(newData);

    if (skipOrderFetch && newData?.Unvan) {
      if (newData.Id) {
        setSelectedCustomerId(String(newData.Id));
        setIsNewCustomer(false);
      } else if (newData.MusteriNo) {
        setSelectedCustomerId(String(newData.MusteriNo));
        setIsNewCustomer(false);
      } else {
        setSelectedCustomerId("new");
        setIsNewCustomer(true);
      }
    }

    // Eğer yeni data'da il bilgisi varsa, selectedIlId'yi de güncelle
    if (newData.Il && iller.length > 0) {
      const existingIl = iller.find((il) =>
        il.IlAdi.toLocaleLowerCase("tr-TR") === newData.Il.toLocaleLowerCase("tr-TR")
      );
      if (existingIl) {
        setSelectedIlId(existingIl.PlakaKodu || existingIl.IlId);
      }
    }
  }, [initialData, iller, skipOrderFetch]);

  const fetchCustomerById = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.baseURL}/musteriler/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const customerData = {
        ...response.data,
        BayiKodu: userData.BayiKodu,
      };

      setData(customerData);
      setSelectedCustomerId(customerData.Id.toString());
      onChange("customer", customerData);
      setIsNewCustomer(false);

      // DÜZELTME 4: Müşteri verisi yüklendiğinde il ID'sini ayarla
      if (customerData.Il && iller.length > 0) {
        const existingIl = iller.find((il) =>
          il.IlAdi.toLocaleLowerCase("tr-TR") === customerData.Il.toLocaleLowerCase("tr-TR")
        );
        if (existingIl) {
          setSelectedIlId(existingIl.PlakaKodu || existingIl.IlId);
        }
      }
    } catch (error) {
      toast.error("Müşteri bilgileri alınamadı");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetails = async () => {
    try {
      const res = await axios.get(`${config.baseURL}/siparis/${orderId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 200) {
        const orderData = res.data;
        setData(orderData);
        setIsNewCustomer(!orderData.MusteriNo);
        setSelectedCustomerId(orderData.MusteriNo || "new");
        onChange("customer", orderData);

        // DÜZELTME 5: Sipariş verisi yüklendiğinde il ID'sini ayarla
        if (orderData.Il && iller.length > 0) {
          const existingIl = iller.find((il) =>
            il.IlAdi.toLocaleLowerCase("tr-TR") === orderData.Il.toLocaleLowerCase("tr-TR")
          );
          if (existingIl) {
            setSelectedIlId(existingIl.PlakaKodu || existingIl.IlId);
          }
        }
      }
    } catch (error) {
      toast.error("Sipariş bilgileri alınamadı");
    }
  };

  const handleCustomerChange = (event) => {
    const value = event.target.value;
    if (value === "new") {
      const cleanState = {
        ...initialNewCustomerState,
        BayiKodu: userData.BayiKodu,
      };
      setData(cleanState);
      setIsNewCustomer(true);
      setSelectedCustomerId("new");
      setSelectedIlId(""); // İl seçimini sıfırla
      onChange("customer", cleanState);
    } else if (value === "select") {
      setDialogOpen(true);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomerId(customer.Id.toString());
    const customerData = {
      ...customer,
      BayiKodu: userData.BayiKodu,
    };
    setData(customerData);
    setIsNewCustomer(false);
    onChange("customer", customerData);
    setDialogOpen(false);
    setSearchQuery("");

    // DÜZELTME 6: Müşteri seçildiğinde il ID'sini ayarla
    if (customerData.Il && iller.length > 0) {
      const existingIl = iller.find((il) =>
        il.IlAdi.toLocaleLowerCase("tr-TR") === customerData.Il.toLocaleLowerCase("tr-TR")
      );
      if (existingIl) {
        setSelectedIlId(existingIl.PlakaKodu || existingIl.IlId);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedData = {
      ...data,
      [name]: value,
    };
    setData(updatedData);
    onChange("customer", {
      ...updatedData,
      BayiKodu: userData.BayiKodu,
    });
  };

  // DÜZELTME 7: İl Autocomplete onChange handler'ını güncelle
  const handleIlChange = (event, newValue) => {
    const ilId = newValue?.PlakaKodu || newValue?.IlId || "";
    setSelectedIlId(ilId);

    const updatedData = {
      ...data,
      Il: newValue?.IlAdi || "",
      Ilce: "", // il değişince ilçe sıfırlanmalı
    };

    setData(updatedData);
    onChange("customer", {
      ...updatedData,
      BayiKodu: userData.BayiKodu,
    });
  };

  // DÜZELTME 8: İlçe Autocomplete onChange handler'ını güncelle
  const handleIlceChange = (event, newValue) => {
    const updatedData = {
      ...data,
      Ilce: newValue?.IlceAdi || "",
    };

    setData(updatedData);
    onChange("customer", {
      ...updatedData,
      BayiKodu: userData.BayiKodu,
    });
  };

  const outerTheme = useTheme();

  const customTheme = (outerTheme) =>
    createTheme({
      palette: {
        ...outerTheme.palette,
        mode: outerTheme.palette.mode,
      },
      typography: {
        ...outerTheme.typography,
      },
      components: {
        MuiTextField: {
          styleOverrides: {
            root: {
              "--TextField-brandBorderColor": outerTheme.palette.mode === 'dark' ? '#424242' : '#D0D0D0',
              "--TextField-brandBorderHoverColor": outerTheme.palette.mode === 'dark' ? '#616161' : '#9E9E9E',
              "--TextField-brandBorderFocusedColor": outerTheme.palette.mode === 'dark' ? '#757575' : '#616161',
              "& label.Mui-focused": {
                color: "var(--TextField-brandBorderFocusedColor)",
              },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: {
              borderColor: "var(--TextField-brandBorderColor)",
            },
            root: {
              [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: "var(--TextField-brandBorderHoverColor)",
              },
              [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: "var(--TextField-brandBorderFocusedColor)",
              },
              "& input": {
                fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
              },
              "& textarea": {
                fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
              },
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            root: {
              "& .MuiSelect-select": {
                fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
              },
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              borderRadius: "8px",
              border: `1px solid ${outerTheme.palette.mode === 'dark' ? '#424242' : '#E0E0E0'}`,
              transition: "none",
            },
          },
        },
      },
    });

  const SectionHeader = ({ icon: Icon, title }) => (
    <Box display="flex" alignItems="center" gap={1.5} mb={2}>
      <Icon
        sx={{
          color: outerTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.54)',
          fontSize: '1.25rem'
        }}
      />
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{
          color: outerTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
          textTransform: 'uppercase',
          fontSize: '0.75rem'
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <div style={{ width: '100%' }}>
      <ThemeProvider theme={customTheme(outerTheme)}>
        <Accordion defaultExpanded elevation={0} sx={{ mb: 1.5, '&:before': { display: 'none' }, width: '100%', backgroundColor:"#FAFAFB"}}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: 2,
              pl: 0,
              pr:1,
              width: '100%',
              '& .MuiAccordionSummary-content': {
                my: 0,
                width: '100%',
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5} width="100%">
              {customer?.customer?.Unvan ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: 'calc(1.7vh * 0.9)'
                  }}
                >
                  {customer.customer.Unvan}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: 'calc(1.7vh * 0.9)'
                  }}
                >
                  Müşteri Seç
                </Typography>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pb: 0, pt: 0, width: '100%' }}>
            <Box sx={{ width: '100%' }}>
              <SectionHeader icon={BusinessIcon} title="Müşteri Bilgileri" />
              <Divider sx={{ mb: 2, mt: 0.5 }} />
              <Card elevation={0} sx={{ mb: 1.5, width: '100%' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, width: '100%' }}>
              <FormControl fullWidth>
                <InputLabel>Müşteri Seç</InputLabel>
                <Select
                  value={selectedCustomerId || ""}
                  onChange={handleCustomerChange}
                  label="Müşteri Seç"
                  disabled={isDisabled || !!musteriId || isEditMode}
                >
                  <MenuItem value="new">Yeni Müşteri</MenuItem>
                  <MenuItem value="select">Müşteri Ara</MenuItem>
                  {data.Unvan && !isNewCustomer && (
                    <MenuItem value={selectedCustomerId}>{data.Unvan}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </CardContent>
          </Card>

        {(isNewCustomer || selectedCustomerId) && (
          <>
            <Card elevation={0} sx={{ mb: 1.5, width: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, width: '100%' }}>
                <SectionHeader icon={BusinessIcon} title="Temel Bilgiler" />
                <Divider sx={{ mb: 2, mt: 0.5 }} />
                <Grid container spacing={2} sx={{ width: '100%' }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Unvan"
                      variant="outlined"
                      required
                      name="Unvan"
                      value={data.Unvan || ""}
                      onChange={handleInputChange}
                      disabled={
                        isDisabled ||
                        !!musteriId ||
                        (!allowEditExistingCustomer && !isNewCustomer && selectedCustomerId !== "new")
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Yetkili"
                      variant="outlined"
                      name="Yetkili"
                      value={data.Yetkili || ""}
                      onChange={handleInputChange}
                      disabled={
                        isDisabled ||
                        !!musteriId ||
                        (!allowEditExistingCustomer && !isNewCustomer && selectedCustomerId !== "new")
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ mb: 1.5, width: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, width: '100%' }}>
                <SectionHeader icon={PhoneIcon} title="İletişim Bilgileri" />
                <Divider sx={{ mb: 2, mt: 0.5 }} />
                <Grid container spacing={2} sx={{ width: '100%' }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="GSM"
                      variant="outlined"
                      name="Tel"
                      type="tel"
                      value={data.Tel || ""}
                      inputProps={{
                        maxLength: 11,
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      error={Boolean(data.Tel && data.Tel.length !== 11)}
                      helperText={
                        data.Tel && data.Tel.length !== 11
                          ? "GSM numarası 11 haneli olmalıdır."
                          : ""
                      }
                      onChange={(e) => {
                        const onlyNumbers = e.target.value.replace(/[^0-9]/g, "");
                        if (onlyNumbers.length <= 11) {
                          handleInputChange({
                            target: { name: "Tel", value: onlyNumbers },
                          });
                        }
                      }}
                      disabled={
                        isDisabled ||
                        !!musteriId ||
                        (!allowEditExistingCustomer && !isNewCustomer && selectedCustomerId !== "new")
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneAndroidIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Telefon"
                      variant="outlined"
                      name="Tel2"
                      type="tel"
                      value={data.Tel2 || ""}
                      inputProps={{
                        maxLength: 11,
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      error={Boolean(data.Tel2 && data.Tel2.length !== 11)}
                      helperText={
                        data.Tel2 && data.Tel2.length !== 11
                          ? "Telefon numarası 11 haneli olmalıdır."
                          : ""
                      }
                      onChange={(e) => {
                        const onlyNumbers = e.target.value.replace(/[^0-9]/g, "");
                        if (onlyNumbers.length <= 11) {
                          handleInputChange({
                            target: { name: "Tel2", value: onlyNumbers },
                          });
                        }
                      }}
                      disabled={
                        isDisabled ||
                        !!musteriId ||
                        (!allowEditExistingCustomer && !isNewCustomer && selectedCustomerId !== "new")
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Fax"
                      variant="outlined"
                      name="Fax"
                      value={data.Fax || ""}
                      inputProps={{
                        maxLength: 11,
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      error={Boolean(data.Fax && data.Fax.length !== 11)}
                      helperText={
                        data.Fax && data.Fax.length !== 11
                          ? "Fax numarası 11 haneli olmalıdır."
                          : ""
                      }
                      onChange={(e) => {
                        const onlyNumbers = e.target.value.replace(/[^0-9]/g, "");
                        if (onlyNumbers.length <= 11) {
                          handleInputChange({
                            target: { name: "Fax", value: onlyNumbers },
                          });
                        }
                      }}
                      disabled={
                        isDisabled ||
                        !!musteriId ||
                        (!allowEditExistingCustomer && !isNewCustomer && selectedCustomerId !== "new")
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <FaxIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      variant="outlined"
                      required
                      type="email"
                      name="EPosta"
                      value={data.EPosta || ""}
                      error={Boolean(
                        data.EPosta && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.EPosta)
                      )}
                      helperText={
                        data.EPosta && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.EPosta)
                          ? "Geçerli bir e-posta adresi giriniz."
                          : ""
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        handleInputChange({ target: { name: "EPosta", value } });
                      }}
                      disabled={
                        isDisabled ||
                        !!musteriId ||
                        (!allowEditExistingCustomer && !isNewCustomer && selectedCustomerId !== "new")
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ mb: 1.5, width: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, width: '100%' }}>
                <SectionHeader icon={ReceiptIcon} title="Vergi Bilgileri" />
                <Divider sx={{ mb: 2, mt: 0.5 }} />
                <Grid container spacing={2} sx={{ width: '100%' }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Vergi Dairesi"
                      variant="outlined"
                      name="VergiDairesi"
                      value={data.VergiDairesi || ""}
                      onChange={handleInputChange}
                      disabled={
                        isDisabled ||
                        !!musteriId ||
                        (!allowEditExistingCustomer && !isNewCustomer && selectedCustomerId !== "new")
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Vergi No"
                      variant="outlined"
                      name="VergiNo"
                      value={data.VergiNo || ""}
                      inputProps={{
                        maxLength: 10,
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      error={Boolean(data.VergiNo && data.VergiNo.length !== 10)}
                      helperText={
                        data.VergiNo && data.VergiNo.length !== 10
                          ? "Vergi Numarası 10 haneli olmalıdır."
                          : ""
                      }
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, "");
                        if (value.length <= 10) {
                          handleInputChange({ target: { name: "VergiNo", value } });
                        }
                      }}
                      disabled={
                        isDisabled ||
                        !!musteriId ||
                        (!allowEditExistingCustomer && !isNewCustomer && selectedCustomerId !== "new")
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ReceiptIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="TC Kimlik No"
                      variant="outlined"
                      name="TCKimlikNo"
                      value={data.TCKimlikNo || ""}
                      inputProps={{
                        maxLength: 11,
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      error={Boolean(data.TCKimlikNo && data.TCKimlikNo.length !== 11)}
                      helperText={
                        data.TCKimlikNo && data.TCKimlikNo.length !== 11
                          ? "TC Kimlik Numarası 11 haneli olmalıdır."
                          : ""
                      }
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, "");
                        if (value.length <= 11) {
                          handleInputChange({ target: { name: "TCKimlikNo", value } });
                        }
                      }}
                      disabled={
                        isDisabled ||
                        !!musteriId ||
                        (!allowEditExistingCustomer && !isNewCustomer && selectedCustomerId !== "new")
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BadgeIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card elevation={0} sx={{ width: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, width: '100%' }}>
                <SectionHeader icon={LocationIcon} title="Adres Bilgileri" />
                <Divider sx={{ mb: 2, mt: 0.5 }} />
                <Grid container spacing={2} sx={{ width: '100%' }}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      fullWidth
                      options={iller}
                      disabled={
                        isDisabled ||
                        !!musteriId ||
                        (!allowEditExistingCustomer && !isNewCustomer && selectedCustomerId !== "new")
                      }
                      getOptionLabel={(option) => option.IlAdi || ""}
                      value={iller.find((il) => il.PlakaKodu === selectedIlId || il.IlId === selectedIlId) || null}
                      onChange={handleIlChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="İl"
                          variant="outlined"
                          disabled={
                            isDisabled ||
                            !!musteriId ||
                            (!allowEditExistingCustomer && !isNewCustomer && selectedCustomerId !== "new")
                          }
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <LocationIcon color="action" fontSize="small" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      fullWidth
                      disabled={
                        isDisabled ||
                        !!musteriId ||
                        (!allowEditExistingCustomer && !isNewCustomer && selectedCustomerId !== "new")
                      }
                      options={ilceler.filter((ilce) => ilce.IlId === selectedIlId)}
                      getOptionLabel={(option) => option.IlceAdi || ""}
                      value={
                        ilceler.find(
                          (ilce) =>
                            ilce.IlceAdi.toLocaleLowerCase("tr-TR") === data.Ilce.toLocaleLowerCase("tr-TR") &&
                            ilce.IlId === selectedIlId
                        ) || null
                      }
                      onChange={handleIlceChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="İlçe"
                          variant="outlined"
                          disabled={
                            isDisabled ||
                            !!musteriId ||
                            (!allowEditExistingCustomer && !isNewCustomer && selectedCustomerId !== "new")
                          }
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <LocationIcon color="action" fontSize="small" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Adres"
                      variant="outlined"
                      name="Adres"
                      value={data.Adres || ""}
                      onChange={handleInputChange}
                      disabled={
                        isDisabled ||
                        !!musteriId ||
                        (!allowEditExistingCustomer && !isNewCustomer && selectedCustomerId !== "new")
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1 }}>
                            <HomeIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </>
        )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </ThemeProvider>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        sx={{ backdropFilter: "blur(5px)" }}
        PaperProps={{
          sx: { minHeight: "600px" },
        }}
      >
        <DialogTitle>Müşteri Ara</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Unvan, Yetkili, Vergi No veya Telefon ile ara"
            variant="outlined"
            value={searchQuery}
            onChange={handleSearchChange}
            margin="normal"
            placeholder="En az 3 karakter giriniz..."
            autoFocus
          />
          <Box sx={{ mt: 2 }}>
            {isSearching ? (
              <Box display="flex" justifyContent="center" my={2}>
                <CircularProgress />
              </Box>
            ) : searchResults.length > 0 ? (
              searchResults.map((customer) => (
                <MenuItem
                  key={customer.Id}
                  onClick={() => handleCustomerSelect(customer)}
                  sx={{
                    display: "block",
                    p: 2,
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <Box>
                    <Box fontWeight="bold">{customer.Unvan}</Box>
                    <Box
                      display="flex"
                      gap={2}
                      color="text.secondary"
                      fontSize="0.875rem"
                    >
                      <span>{customer.Yetkili}</span>
                      {customer.VergiNo && <span>VN: {customer.VergiNo}</span>}
                      {customer.Tel && <span>{customer.Tel}</span>}
                    </Box>
                  </Box>
                </MenuItem>
              ))
            ) : searchQuery.length > 1 ? (
              <Box p={2} textAlign="center" color="text.secondary">
                Sonuç bulunamadı
              </Box>
            ) : (
              <Box p={2} textAlign="center" color="text.secondary">
                Arama yapmak için en az 3 karakter giriniz
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CustomerStep;